import React from 'react';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { format } from 'date-fns';
import constants from 'utils/constants';
import InfoCard from './InfoCard';
import SynchCard from './SynchCard';
import WalletSyncCard from './WalletSyncCard';
import LoaderLogo from '../../common/ErgoLoader/index';

const getWalletStatus = (isWalletInitialized) => {
  if (!isWalletInitialized) return 'Not initialized';
  return 'Initialized';
};

const DashboardView = ({
  error,
  nodeInfo,
  isWalletInitialized,
  walletStatusData,
  walletBalanceData,
  ergPrice,
}) => {
  if (error !== null) {
    return (
      <div className="container-fluid vh-100 d-flex align-items-center justify-content-center">
        <h3 className="text-danger">
          <FontAwesomeIcon icon={faExclamationTriangle} /> {error}
        </h3>
      </div>
    );
  }

  if (nodeInfo === null) {
    return (
      <div className="container-fluid vh-100 d-flex align-items-center justify-content-center">
        <LoaderLogo />
      </div>
    );
  }

  const {
    network,
    peersCount,
    bestHeaderId,
    launchTime,
    headersHeight,
    fullHeight,
    maxPeerHeight,
    appVersion,
    isMining,
  } = nodeInfo;

  const syncStage = fullHeight === null ? 'header' : 'block';

  let progress = null;
  if (fullHeight === null && maxPeerHeight !== null) {
    progress = ((headersHeight / maxPeerHeight) * 100).toFixed(2);
  } else if (fullHeight !== null) {
    progress = ((fullHeight / headersHeight) * 100).toFixed(2);
  }

  const explorer = `https://${
    network === 'mainnet' ? 'explorer' : network
  }.ergoplatform.com/en/blocks/${bestHeaderId}`;

  const renderCard = (children) => (
    <div className="dashboard__item">{children}</div>
  );

  return (
    <>
      {/* Node Info */}
      <div className="dashboard">
        <h2 className="dashboard__title">Node Information</h2>
        <div
          className="dashboard__container"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '16px',
          }}
        >
          {renderCard(
            <InfoCard className="rounded-0 shadow-none">
              <p className="info-card__title">Version</p>
              <p className="info-card__label">{appVersion}</p>
            </InfoCard>,
          )}
          {renderCard(<SynchCard nodeInfo={nodeInfo} />)}
          {renderCard(
            <InfoCard className="rounded-0 shadow-none">
              <p className="info-card__title">Started at</p>
              <p className="info-card__label">
                {format(new Date(launchTime), 'MM-dd-yyyy HH:mm:ss')}
              </p>
            </InfoCard>,
          )}
          {fullHeight !== null &&
            renderCard(
              <InfoCard className="rounded-0 shadow-none">
                <p className="info-card__title">Current height</p>
                <p className="info-card__label">{fullHeight}</p>
              </InfoCard>,
            )}
          {bestHeaderId !== null &&
            renderCard(
              <InfoCard className="rounded-0 shadow-none">
                <p className="info-card__title">Best block id</p>
                <p className="info-card__label">
                  <a href={explorer} target="_blank" rel="noopener noreferrer">
                    {bestHeaderId}
                  </a>
                </p>
              </InfoCard>,
            )}
          {renderCard(
            <InfoCard className="rounded-0 shadow-none">
              <p className="info-card__title">Mining enabled</p>
              <p className="info-card__label">{isMining ? 'Yes' : 'No'}</p>
            </InfoCard>,
          )}
          {renderCard(
            <InfoCard className="rounded-0 shadow-none">
              <p className="info-card__title">Peers connected</p>
              <p className="info-card__label">{peersCount}</p>
            </InfoCard>,
          )}
          {progress && progress !== 100 &&
            renderCard(
              <InfoCard className="rounded-0 shadow-none">
                <p className="info-card__title">Sync progress</p>
                <p className="info-card__label">
                  Syncing {syncStage}s: {progress > 100 ? '--' : `${progress}%`}
                </p>
                <span
                  className="info-card__progress"
                  style={{
                    background: `linear-gradient(90deg, var(--${syncStage}) ${
                      progress > 100 ? 0 : progress
                    }%, black 0%)`,
                  }}
                />
              </InfoCard>,
            )}
        </div>
      </div>

      {/* ERG Info */}
      {ergPrice && (
        <div className="dashboard">
          <h2 className="dashboard__title">ERG Information</h2>
          <div
            className="dashboard__container"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: '16px',
            }}
          >
            {renderCard(
              <InfoCard className="rounded-0 shadow-none">
                <p className="info-card__title">
                  ERG price in $ <br />
                  (based on oracle pool data)
                </p>
                <p className="info-card__label">{ergPrice}</p>
              </InfoCard>,
            )}
          </div>
        </div>
      )}

      {/* Wallet Info */}
      {walletStatusData && (
        <div className="dashboard">
          <h2 className="dashboard__title">Wallet Information</h2>
          <div
            className="dashboard__container"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: '16px',
            }}
          >
            {renderCard(
              <InfoCard className="rounded-0 shadow-none">
                <p className="info-card__title">Initialization state</p>
                <p className="info-card__label">{getWalletStatus(isWalletInitialized)}</p>
              </InfoCard>,
            )}
            {renderCard(
              <InfoCard className="rounded-0 shadow-none">
                <p className="info-card__title">Lock state</p>
                <p className="info-card__label">
                  {walletStatusData.isUnlocked ? 'Unlocked' : 'Locked'}
                </p>
              </InfoCard>,
            )}
            {renderCard(
              <WalletSyncCard
                walletStatusData={walletStatusData}
                headersHeight={nodeInfo.headersHeight}
              />,
            )}
            {walletBalanceData &&
              renderCard(
                <InfoCard className="rounded-0 shadow-none">
                  <p className="info-card__title">Balance</p>
                  <p className="info-card__label">
                    {walletBalanceData.balance / constants.nanoErgInErg} ERG{' '}
                    {ergPrice &&
                      `~ $${Number(
                        ergPrice * (walletBalanceData.balance / constants.nanoErgInErg),
                      ).toFixed(2)}`}
                  </p>
                </InfoCard>,
              )}
            {walletBalanceData &&
              renderCard(
                <InfoCard className="rounded-0 shadow-none">
                  <p className="info-card__title">Assets</p>
                  <p className="info-card__label">
                    {Object.values(walletBalanceData.assets).length || '0'}
                  </p>
                </InfoCard>,
              )}
          </div>
        </div>
      )}
    </>
  );
};

export default DashboardView;

